<template>
  <div class="inventory page page-with-table p-2">
    <page-header class="mb-2" :title="$t('Inventory')">
      <template v-slot:buttons>
        <b-button variant="primary" @click="onCreateClick" class="mr-2" v-permission="'change_inventoryitem'">
          <font-awesome-icon icon="plus-circle" class="icon"/>
          {{ $t('Create Inventory Item') }}
        </b-button>
        <input type="file" @change="onZipFileChange" ref="zipFileRef" class="d-none"
               accept="zip,application/octet-stream,application/zip,application/x-zip,application/x-zip-compressed">
        <b-dropdown variant="primary" right :text="$t('Actions')" v-permission="'change_inventoryitem'">
          <b-dropdown-item @click="showQRGenerateModal()">
            <font-awesome-icon icon="qrcode" class="mr-2 icon"/>
            {{ $t('Generate Blank QR Codes') }}
          </b-dropdown-item>
          <b-dropdown-divider></b-dropdown-divider>
          <upload-images-dropdown-item :disabled="!tableData['rows'].length"/>
          <div id="upload-zip-dropdown-item">
            <b-dropdown-item @click="$refs.zipFileRef.click()"
                             :disabled="isUploading || !tableData['rows'].length || true">
              <font-awesome-icon icon="upload" class="icon mr-2"/>
              {{ $t('Upload ZIP') }}
            </b-dropdown-item>
          </div>
          <b-popover target="upload-zip-dropdown-item" triggers="hover" placement="right">
            <template v-slot:title>{{ $t('Warning') }}</template>
            <span class="text-danger">
              {{
                $t('Upload ZIP functionality temporarily disabled due to maintenance. Please use Upload Images feature instead.')
              }}
            </span>
          </b-popover>
          <inventory-bulk-upload/>
          <inventory-bulk-edit-upload/>
        </b-dropdown>
      </template>
    </page-header>
    <div class="card content-wrapper p-2">
      <InventoryTable :exclude-header-actions="excludeActions" :exclude-search-fields="excludeSearchFields"
                      enable-frontend-export/>
    </div>
    <InventoryModal/>
    <QRGenerateModal/>
  </div>
</template>

<script>
  import InventoryTable from "./InventoryTable";
  import {createNamespacedHelpers} from "vuex";
  import InventoryModal from "./InventoryModal";
  import QRGenerateModal from "./QRGenerateModal";
  import PageHeader from "../../core/components/page-header/PageHeader";
  import InventoryBulkUpload from "@/modules/inventory/InventoryBulkUpload";
  import InventoryBulkEditUpload from "@/modules/inventory/InventoryBulkEditUpload";
  import {Utils} from "@/core/services/utils.service";
  import InventoryService from "@/modules/inventory/InventoryService";
  import UploadImagesDropdownItem from "@/modules/inventory/UploadImagesDropdownItem";
  import {ACTION_INVENTORY_GROUP_ITEMS, ACTION_INVENTORY_RETURN_ITEMS} from "@/modules/constants";

  const {mapActions, mapState} = createNamespacedHelpers('inventory');
  const {mapState: mapStateGlobal, mapActions: mapActionsGlobal} = createNamespacedHelpers('global');

  export default {
    name: "Inventory",
    components: {
      UploadImagesDropdownItem,
      InventoryBulkUpload,
      InventoryBulkEditUpload,
      PageHeader,
      InventoryModal,
      InventoryTable,
      QRGenerateModal
    },
    data: () => {
      return {
        isUploading: false,
        excludeActions: [ACTION_INVENTORY_GROUP_ITEMS, ACTION_INVENTORY_RETURN_ITEMS],
        excludeSearchFields: []
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
      }),
      ...mapStateGlobal({
        imagesZipMaxFileSize: state => state.imagesZipMaxFileSize,
        bulkImagesMaxFileCount: state => state.bulkImagesMaxFileCount,
      })
    },
    methods: {
      ...mapActions(['showModal', 'updateTableSource', 'showQRGenerateModal']),
      ...mapActionsGlobal(['addProgress', 'removeProgress']),
      handleUploadResponse(response) {
        const nonFieldErrors = response.body.non_field_errors;
        if (response.success) {
          if (!nonFieldErrors) {
            this.$toast('success', this.$i18n.t('Images were uploaded successfully'));
          } else {
            this.$toast('success', this.$i18n.t('Images were uploaded partially. Please check error message.'));
          }
        }

        if (nonFieldErrors) {
          if (Array.isArray(nonFieldErrors)) {
            Utils.showErrorsInDialog(nonFieldErrors, this);
          } else {
            const dataArr = [];
            for (let item_no in nonFieldErrors) {
              const listItemsArr = [];
              if (nonFieldErrors[item_no] instanceof Array) {
                for (const imageName of nonFieldErrors[item_no]) {
                  const msg = `${imageName} - ${nonFieldErrors[item_no][imageName]}`;
                  listItemsArr.push(this.$createElement('li', {}, msg));
                }
              } else if (nonFieldErrors[item_no] instanceof Object) {
                for (const imageName in nonFieldErrors[item_no]) {
                  const source = nonFieldErrors[item_no][imageName]
                  // TODO should be rewritten in better way
                  let msg
                  if (source instanceof Array) {
                    msg = `${imageName} - ${source.join(', ')}`;
                  } else if (typeof source === "string") {
                    msg = source
                  } else {
                    // TODO handle error
                    continue
                  }
                  listItemsArr.push(this.$createElement('li', {}, msg));
                }
              } else if (typeof nonFieldErrors[item_no] === "string") {
                listItemsArr.push(this.$createElement('li', {}, nonFieldErrors[item_no]));
              }

              dataArr.push(this.$createElement('h5', {class: "mb-0"}, `Item ${item_no}`));
              dataArr.push(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
            }
            this.$errorDialog(this.$createElement('div', {}, dataArr), this.$i18n.t('Image Validation Error'));
          }
        }
      },
      async onZipFileChange($event) {
        if (!$event.target.files || !$event.target.files[0]) {
          return false;
        }

        this.isUploading = true;

        const zipFile = $event.target.files[0];
        const sizeInMbs = zipFile.size / 1000000; // Convert bytes to megabytes

        if (sizeInMbs > this.imagesZipMaxFileSize) {
          this.$errorDialog(
            this.$i18n.t("Zip file size limit exceeded. Max size allowed {size} mbs.", {size: this.imagesZipMaxFileSize}),
            this.$i18n.t('Zip File Validation')
          );
          this.isUploading = false;
          this.$refs.zipFileRef.value = null;
          return;
        }

        // let formData = new FormData();
        // formData.append('images', zipFile);

        // let progress = await this.addProgress(zipFile.name);
        // let response = await InventoryService.createInventoryImagesFromZip(formData, (progressEvent) => {
        //   this.zipFileProgressValue = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
        //   progress.progressValue = this.zipFileProgressValue;
        //   if (this.zipFileProgressValue === 100) {
        //     this.removeProgress(progress.uuid);
        //   }
        // });
        let presignedUrl, base64, blob, response
        try {
          presignedUrl = (await InventoryService.getPresignedUrlForFileUpload('zip')).body
          base64 = await InventoryService.convertEventTargetToBase64(zipFile)
          blob = await InventoryService.convertBase64ToBlob(base64)
          response = await InventoryService.uploadBlobToBucket(presignedUrl, blob)
        } catch (err) {
          this.$errorDialog(this.$i18n.t('Something went wrong when uploading zip'));
          this.isUploading = false;
          this.$refs.zipFileRef.value = null;
          return
        }

        this.handleUploadResponse(response);
        this.isUploading = false;
        this.$refs.zipFileRef.value = null;
      },
      onCreateClick() {
        this.showModal({object: {}});
      },
    },
    async beforeMount() {
      await this.updateTableSource({source: 'inventory'});
    }
  }
</script>

<style lang="scss" scoped>
</style>
